import { action, observable, runInAction } from "mobx";
import { ITest } from "src/Interfaces/TestInterface";
import { IWell } from "src/Interfaces/WellInterface";
import { API } from "src/Services/Api/Api";
import PdfService from '../../Services/Pdf/PdfService';
import { AdminStore } from "../Admin/AdminStore";
import { CalcStore } from "../Calc/CalcStore";
import { DispatchStore } from "../Dispatch/DispatchStore";
import { ErrorsStore } from "../Errors/Errors";
import { MessageStore } from "../Messages/Messages";
import { SyncStore } from "../Sync/SyncStore";
import TenantStore from "../Tenant/TenantStore";
import { TestsStore } from "../Tests/TestsStore";
import { UserStore } from "../User/UserStore";
import { WellsStore } from "../Wells/WellsStore";

export class RootStore {
    public UserStore: UserStore;
    public TestsStore: TestsStore;
    public WellsStore: WellsStore;
    public MessageStore: MessageStore;
    public SyncStore: SyncStore;
    public ErrorsStore: ErrorsStore;
    public CalcStore: CalcStore;
    public AdminStore: AdminStore;
    public DispatchStore: DispatchStore;
    public TenantStore: TenantStore;
    public Api: API;

    @observable
    public loading = false;

    @observable
    public syncingOffline = false;

    @observable
    public syncLoginFailed = false;

    public loadingCount = 0;

    constructor() {
        this.Api = new API(this);
        this.MessageStore = new MessageStore();
        this.CalcStore = new CalcStore();
        this.UserStore = new UserStore(this);
        this.TestsStore = new TestsStore(this);
        this.WellsStore = new WellsStore(this);
        this.AdminStore = new AdminStore(this);
        this.ErrorsStore = new ErrorsStore(this);
        this.SyncStore = new SyncStore(this);
        this.DispatchStore = new DispatchStore(this);
        this.TenantStore = new TenantStore(this);
        this.Api.setInstanceStores();
        this.TenantStore.getTenant();
    }
    
    public addLoading = () => {
        if (this.loadingCount++ === 0 ){
            setTimeout(() => this.checkLoading(), 500);
        }
    }

    public removeLoading = () => {
        if (--this.loadingCount === 0) {
            this.checkLoading();
        }
    }

    @action
    public checkLoading = () => {
        this.loading = false;
        if (this.loadingCount > 0) {
            this.loading = true;
        }
    }
    
    @action public setSyncLoginFailed = (val: boolean) => {
        this.syncLoginFailed = val;
    }
    public getAllDataForOffline = async () => {

        if (this.syncingOffline) {
            return;
        }
        if(this.UserStore.hasGenericPermission("Permissions.Wells.Read")) {
            runInAction(() => {
                this.syncingOffline = true;
            });
            this.MessageStore.addMessage('Downloading offline data...', 'success', 2000);
            const contractors = await this.Api.getFetchWithHeaders('/contractors')
                .then(data => {
                    if (data && Array.isArray(data)) {
                        const contacts = data.map(cont => this.Api.getFetchWithHeaders(`/contractors/${cont.id}/contacts`));
                        return Promise.all(contacts);
                    }
                    return Promise.all([]);
                });
            
            const operators = await this.Api.getFetchWithHeaders('/operators')
                .then(data => {
                    if (data && Array.isArray(data)) {
                        const contacts = data.map(cont => this.Api.getFetchWithHeaders(`/operators/${cont.id}/contacts`));
                        return Promise.all(contacts);
                    }
                    return Promise.all([]);
                });
            
            const states = await this.Api.getFetchWithHeaders('/static/usstates')
                .then(data => {
                    if (data && Array.isArray(data)) {
                        const counties = data.map(st => this.Api.getFetchWithHeaders('/static/counties/' + st));
                        return Promise.all(counties);
                    }
                    return Promise.all([]);
                });
            
            const engineers = this.Api.getFetchWithHeaders('/account/engineers');
            const warehouses = this.Api.getFetchWithHeaders('/warehouses');
            const otherContacts = this.Api.getFetchWithHeaders('/othercontacts');
            const novaMudEmailList = this.Api.getFetchWithHeaders('/optionalfieldlists/InternalEmailGroupOptions');
            const targetFormationOptions = this.Api.getFetchWithHeaders('/optionalfieldlists/TargetFormationOptions');
            const masterExpenses = this.Api.getFetchWithHeaders('/expensetypes');
            const masterProducts = this.Api.getFetchWithHeaders('/products/inventory');
            const mudTypes = this.Api.getFetchWithHeaders('/mudtypes');
            const deviceNames = this.Api.getFetchWithHeaders('/solidscontrolequip');
            const intervalTypes = this.Api.getFetchWithHeaders('/optionalfieldlists/intervalTypeOptions');
            const subIntervalTypes = this.Api.getFetchWithHeaders('/optionalfieldlists/subIntervalTypeOptions');
            const toolDesc = this.Api.getFetchWithHeaders('/optionalfieldlists/ToolsDescriptionOptions');
            const drillItemTypes = this.Api.getFetchWithHeaders('/drillstrings/itemtypes');
            const productCategories = this.Api.getFetchWithHeaders('/categorys');
            const mudPropOptions = this.Api.getFetchWithHeaders('/optionalfieldlists/mudpropertyoptions');
            const mudTestOptions = this.Api.getFetchWithHeaders('/optionalfieldlists/mudtestoptions');
            const storageTypeOptions = this.Api.getFetchWithHeaders('/optionalfieldlists/storagetypeoptions');
            const oilSubTypeOptions = this.Api.getFetchWithHeaders('/optionalfieldlists/OilFluidSubTypeOptions');
            const waterSubTypeOptions = this.Api.getFetchWithHeaders('/optionalfieldlists/WaterFluidSubTypeOptions');
            const taxRates = this.Api.getFetchWithHeaders('/products/taxrate');
            const wells: IWell[] = await this.Api.getFetchWithHeaders('/wells');
                
            if (wells && Array.isArray(wells)) {
                for (const well of wells) {
                    if (well.wellStatus === 'Active') {
                        await Promise.all([
                            this.Api.getFetchWithHeaders('/wells/' + well.id),
                            this.Api.getFetchWithHeaders('/products/inventory/' + well.id),
                            this.Api.getFetchWithHeaders('/expensetypes/inventory/' + well.id),
                            this.Api.getFetchWithHeaders('/products/weightingagents/' + well.id),
                        ]);
                    
                        if (
                            this.UserStore.hasPermission("Permissions.WellTests.Read.All")
                            || (
                                this.UserStore.hasPermission("Permissions.WellTests.Read.Assigned")
                                && well.engineerIds
                                && well.engineerIds.some(ei => ei === this.UserStore.user.id)
                            )
                        ) {
                            const tests = await this.Api.getFetchWithHeaders(`/welltests/${well.id}/tests`);
                            if (tests !== undefined && Array.isArray(tests)) {
                                for (const test of tests) {
                                    await this.Api.getFetchWithHeaders(`/welltests/${well.id}/tests/${test.id}`);
                                }
                            }
                        }
                    }
                }
            }

            Promise.all([
                contractors,
                operators,
                states,
                engineers,
                warehouses,
                otherContacts,
                novaMudEmailList,
                targetFormationOptions,
                masterExpenses,
                masterProducts,
                mudTypes,
                deviceNames,
                intervalTypes,
                subIntervalTypes,
                toolDesc,
                drillItemTypes,
                productCategories,
                mudPropOptions,
                mudTestOptions,
                storageTypeOptions,
                taxRates,
                oilSubTypeOptions,
                waterSubTypeOptions,
                wells,
            ]).then(data => {
                if(data) {
                    this.MessageStore.addMessage('Offline Data Ready', 'success', 1000);
                }
                runInAction(() => {
                    this.syncingOffline = false;
                });
            }).catch(error => {
                if(error) {
                    this.MessageStore.addMessage('Data Download failed error: ' + error, 'danger');
                }
                runInAction(() => {
                    this.syncingOffline = false;
                });
            });
        }
    }

    public emailMudReportPdfFromServer = async (
        test: ITest,
        well: IWell,
        body: any,
        sendMud: boolean,
        sendVolume: boolean,
        sendExternal: boolean,
        sendVolumeSnapshot: boolean,
        sendWellStory: boolean,
        sendProductSnapshot: boolean
        ) => {
        let testList;
        if (sendVolume || sendExternal) {
            testList = await this.TestsStore.getAllTestsDetailsReport(well.id);
        }
        let wellCloseReview;
        if (sendVolume && well.wellStatus !== 'Active') {
            wellCloseReview = await this.WellsStore.getWellCloseReview();
        }
        body.FromEmail = this.UserStore.user.notificationEmail;
        body.CreatedEmail = test.mudReportCreateUser ? test.mudReportCreateUser.notificationEmail : this.UserStore.user.notificationEmail;
        body.file = sendMud ? await PdfService.createMudPdfBlob(test, well, this.TenantStore.logoPathNameReport, false) : null; 
        body.volumeFile = sendVolume ? await PdfService.createVolumeBlob(testList, well, wellCloseReview, this.TenantStore.logoPathNameReport) : null;
        body.externalFile = sendExternal ? await PdfService.createExternalBlob(testList, well, this.TenantStore.logoPathNameReport) : null;
        body.wellStoryFile = sendWellStory ? await PdfService.createWellStoryBlob(test, well, this.TenantStore.tenant) : null;
        body.volumeSnapshotFile = sendVolumeSnapshot ? await PdfService.createVolumeSnapshotBlob(test, well, this.TenantStore.logoPathNameReport) : null;
        body.productSnapshotFile = sendProductSnapshot ? await PdfService.createProductInventorySnapshotBlob(test, well, this.TenantStore.logoPathNameReport) : null;
        body.wellTestId = test.id;
        body.WellId = well.id;
        body.PresentActivity = test.presentActivity;
        body.WellName = well.wellName;
        body.ContractorName = well.contractor.contractorName;
        body.OperatorName = well.operator.operatorName;
        body.RigNumber = well.rigNumber;
        body.DepthMD = test.depthMD;
        body.DepthTVD = test.depthTVD;
        body.BaseOilUsed = test.volumeAccounting.baseOilUsed;
        body.CumBaseOilUsed = test.volumeAccounting.cumBaseOilUsed;
        body.MudProperty = test.mudProperty;
        body.PropertyType = test.propertyType;
        body.MudWeight = test.mudProperties.mudWeight;
        body.FunnelViscosity = test.mudProperties.funnelViscosity;
        body.ChlorideMgL = test.mudProperties.chlorideMgL;
        body.LowGravityPcnt = test.mudProperties.lowGravityPcnt;
        body.FiltrateHthp = test.mudProperties.filtrateHthp;
        body.FiltrateApi = test.mudProperties.filtrateApi;
        body.DailyTotal = test.inventoryTotal.dailyTotal;
        body.CumulativeTotal = test.inventoryTotal.cumulativeTotal;
        body.MudReportRecap = test.remarks.mudReportRecap;
        body.OilBased = test.mudProperty === 'Oil Based Mud';
        return this.Api.file(`/Reports/${well.id}/mudreport/${test.id}/email`, body);
    }

    public startWellClose = () => {
        return this.WellsStore.startWellClose().then((data) => {
            if (data) {
                return this.WellsStore.getWellDetails(this.WellsStore.well.id);
            }
            return;
        });
    }
}


// WEBPACK FOOTER //
// ./src/Stores/Root/RootStore.ts